package cz.vojtechhoranek.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.toWidth
import com.varabyte.kobweb.silk.style.toAttrs
import cz.vojtechhoranek.site.components.layouts.PageLayout
import cz.vojtechhoranek.site.components.widgets.ProjectGallery
import cz.vojtechhoranek.site.components.widgets.ContactButtons
import cz.vojtechhoranek.site.toSitePalette
import cz.vojtechhoranek.site.localization.LocalSiteText
import cz.vojtechhoranek.site.localization.TimelineEntry
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

val AboutPageStyle = CssStyle {
    base { Modifier.fillMaxWidth().padding(top = 3.cssRem, bottom = 4.cssRem) }
    cssRule("h1") {
        Modifier.margin(0.px).fontSize(36.px).fontWeight(500).lineHeight(1.2)
    }
    cssRule("h2") {
        Modifier.margin(top = 3.cssRem, bottom = 2.cssRem).fontSize(22.px).fontWeight(500)
    }
    cssRule("h3") {
        Modifier.margin(0.px).fontSize(20.px).fontWeight(500).lineHeight(1.4)
    }
    cssRule("p") {
        Modifier.margin(top = 0.75.cssRem, bottom = 0.px).fontSize(16.px).lineHeight(1.7)
    }
    cssRule(" .about-meta") {
        Modifier.color(colorMode.toSitePalette().onSurfaceVariant).fontSize(14.px).lineHeight(1.6)
    }
    cssRule(" .about-links") {
        Modifier.display(DisplayStyle.Flex).gap(1.25.cssRem).flexWrap(FlexWrap.Wrap)
            .margin(top = 1.25.cssRem).fontSize(14.px)
    }
    cssRule(" .about-honors") {
        Modifier.color(colorMode.toSitePalette().primary).fontSize(14.px)
    }
    cssRule("ol") {
        Modifier.margin(0.px).padding(0.px).styleModifier { property("list-style", "none") }
    }
    cssRule(" .about-volunteering") {
        Modifier.borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().outlineVariant)
            .padding(top = 1.5.cssRem)
    }
    cssRule(" .about-volunteering h2") {
        Modifier.margin(top = 0.px, bottom = 1.5.cssRem)
    }
}

val AboutHeaderStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(1.5.cssRem)
            .styleModifier { property("grid-template-columns", "minmax(0, 1fr) 5rem") }
    }
    Breakpoint.MD {
        Modifier.columnGap(3.cssRem)
            .styleModifier { property("grid-template-columns", "minmax(0, 1fr) 10rem") }
    }
    cssRule("img") {
        Modifier.fillMaxWidth().borderRadius(1.cssRem)
            .styleModifier { property("height", "auto") }
    }
}

val AboutPhotoStyle = CssStyle {
    base { Modifier.gridColumn(2).gridRow(1) }
    Breakpoint.MD { Modifier.styleModifier { property("grid-row", "1 / 3") } }
}

val AboutContactStyle = CssStyle {
    base { Modifier.styleModifier { property("grid-column", "1 / -1") } }
    Breakpoint.MD { Modifier.gridColumn(1).gridRow(2) }
}

val AboutTimelineItemStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).columnGap(1.cssRem)
            .styleModifier { property("grid-template-columns", "0.75rem minmax(0, 1fr)") }
    }
    Breakpoint.MD {
        Modifier.columnGap(1.5.cssRem)
            .styleModifier { property("grid-template-columns", "8rem 0.75rem minmax(0, 1fr)") }
    }
}

val AboutTimelineDateStyle = CssStyle {
    base {
        Modifier.gridColumn(2).gridRow(1).padding(bottom = 0.5.cssRem)
            .color(colorMode.toSitePalette().onSurfaceVariant).fontSize(14.px).lineHeight(1.6)
    }
    Breakpoint.MD { Modifier.gridColumn(1).padding(top = 0.15.cssRem) }
}

val AboutTimelineContentStyle = CssStyle {
    base { Modifier.gridColumn(2).gridRow(2).padding(bottom = 2.5.cssRem).minWidth(0.px) }
    Breakpoint.MD { Modifier.gridColumn(3).gridRow(1) }
    cssRule(" .about-entry-kind") {
        Modifier.display(DisplayStyle.Block).margin(bottom = 0.4.cssRem)
            .color(colorMode.toSitePalette().onSurfaceVariant).fontSize(12.px)
            .letterSpacing(0.08.cssRem).styleModifier { property("text-transform", "uppercase") }
    }
}

val AboutTimelineRailStyle = CssStyle {
    base {
        Modifier.gridColumn(1).position(Position.Relative)
            .styleModifier { property("grid-row", "1 / 3") }
    }
    Breakpoint.MD { Modifier.gridColumn(2).gridRow(1) }
    cssRule(" .timeline-line") {
        Modifier.position(Position.Absolute).top(0.5.cssRem).bottom((-0.5).cssRem)
            .left(5.px).width(1.px).backgroundColor(colorMode.toSitePalette().outlineVariant)
    }
    cssRule(" .timeline-dot") {
        Modifier.position(Position.Absolute).top(0.4.cssRem).left(0.px)
            .size(11.px).borderRadius(50.percent)
            .border(2.px, LineStyle.Solid, colorMode.toSitePalette().primary)
            .backgroundColor(colorMode.toSitePalette().background)
    }
    cssRule(" .timeline-dot-current") {
        Modifier.backgroundColor(colorMode.toSitePalette().primary)
    }
}

@Composable
private fun TimelineItem(entry: TimelineEntry, current: Boolean, last: Boolean) {
    Li(AboutTimelineItemStyle.toAttrs()) {
        Div(AboutTimelineDateStyle.toAttrs()) { Text(entry.date) }
        Div(AboutTimelineRailStyle.toAttrs { attr("aria-hidden", "true") }) {
            if (!last) Div({ classes("timeline-line") })
            Div({
                classes("timeline-dot")
                if (current) classes("timeline-dot-current")
            })
        }
        Div(AboutTimelineContentStyle.toAttrs()) {
            Span({ classes("about-entry-kind") }) { Text(entry.kind) }
            H3 { Text(entry.title) }
            P({ classes("about-meta") }) {
                if (entry.organizationUrl != null) {
                    Link(entry.organizationUrl, entry.organization)
                } else {
                    Text(entry.organization)
                }
            }
            P { Text(entry.description) }
            entry.honors?.let { honors ->
                P({ classes("about-honors") }) { Text(honors) }
            }
            if (entry.links.isNotEmpty()) {
                Div({ classes("about-links") }) {
                    entry.links.forEach { link -> Link(link.url, link.text) }
                }
            }
            entry.gallery?.let { ProjectGallery(it) }
        }
    }
}

@Page("/")
@Composable
fun HomePage() {
    val text = LocalSiteText.current

    PageLayout(text.about) {
        Article(AboutPageStyle.toAttrs()) {
            Header(AboutHeaderStyle.toAttrs()) {
                Div {
                    H1 { Text(text.about) }
                    P { Text(text.introduction) }
                }
                Img(src = "/vojtech-horanek.webp", alt = "", attrs = AboutPhotoStyle.toAttrs {
                    attr("width", "320")
                    attr("height", "320")
                    attr("srcset", "/vojtech-horanek-160.webp 160w, /vojtech-horanek.webp 320w, /vojtech-horanek-480.webp 480w")
                    attr("sizes", "(min-width: ${Breakpoint.MD.toWidth()}) 10rem, 5rem")
                })
                Div(AboutContactStyle.toAttrs()) { ContactButtons() }
            }
            Section({ attr("aria-labelledby", "work-education") }) {
                H2({ id("work-education") }) { Text(text.workAndEducation) }
                Ol {
                    text.timeline.forEachIndexed { index, entry ->
                        TimelineItem(entry, current = index == 0, last = index == text.timeline.lastIndex)
                    }
                }
            }
            Section({ attr("aria-labelledby", "talks") }) {
                H2({ id("talks") }) { Text(text.talksHeading) }
                Ol {
                    text.talks.forEachIndexed { index, entry ->
                        TimelineItem(entry, current = false, last = index == text.talks.lastIndex)
                    }
                }
            }
            Section({ classes("about-volunteering"); attr("aria-labelledby", "outside-work") }) {
                H2({ id("outside-work") }) { Text(text.outsideWork) }
                Ol {
                    text.outsideWorkEntries.forEachIndexed { index, entry ->
                        TimelineItem(entry, current = index == 0, last = index == text.outsideWorkEntries.lastIndex)
                    }
                }
            }
        }
    }
}
